import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  FormControl,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  Paper,
  Typography
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useLocation } from 'react-router-dom';
import anearApi from '../Api/AnearApi';
import UserAuthContainer from '../Containers/UserAuthContainer';
import useNavigation from '../Utils/navigationUtils';
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import PageBackRow from '../UI/PageBackRow'

const ResetPasswordPage = () => {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(token ? null : new Error('Password reset link is missing or invalid.'));
  const [submitting, setSubmitting] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const userAuthContainer = UserAuthContainer.useContainer();
  const { navigateToLogin, navigateToPathWithReplace, resolveHomePath } = useNavigation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;

    try {
      setSubmitting(true);
      setError(null);
      const user = await anearApi.resetPassword(token, password);
      await userAuthContainer.setUser(user);
      setResetComplete(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const continueHandler = () => {
    navigateToPathWithReplace(resolveHomePath());
  };

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        {!resetComplete && (
          <PageBackRow onClick={navigateToLogin} label="Back to login" sx={{ mb: 1 }} />
        )}
        <Avatar sx={{ m: 1, backgroundColor: 'rgba(124, 58, 237, 0.3)', color: '#A78BFA' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2 }}>
          Choose a new password
        </Typography>

        {!resetComplete && (
          <Typography
            variant="body2"
            sx={{ mt: 1, color: 'rgba(230,233,239,0.82)', textAlign: 'center', maxWidth: '30rem' }}
          >
            Enter your new password below to finish resetting your account credentials.
          </Typography>
        )}

        {!resetComplete && (
          <Box component="form" sx={{ width: '100%', mt: 2 }} autoComplete="off">
            <FormControl
              required
              fullWidth
              error={error !== null}
              sx={{ mb: 2, '& label + .MuiInputBase-root': { marginTop: 0 } }}
              variant="standard"
            >
              <InputLabel
                htmlFor="password"
                shrink={true}
                sx={{
                  color: '#06B6D4',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  transform: 'none',
                  position: 'static',
                  mb: 0.25
                }}
              >
                New Password
              </InputLabel>
              <Input
                name="password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                required
                value={password}
                sx={{
                  color: '#E6E9EF',
                  marginTop: '0 !important',
                  '&::before': { borderBottom: '1px solid rgba(230,233,239,0.3)' },
                  '&:hover:not(.Mui-disabled)::before': { borderBottom: '1px solid rgba(230,233,239,0.5)' },
                  '&::after': { borderBottom: '2px solid #7C3AED' }
                }}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ color: 'rgba(230,233,239,0.7)' }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            {error && (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)'
                }}
              >
                <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 500, lineHeight: 1.5 }}>
                  {error.message || 'Unable to reset your password. Please try again.'}
                </Typography>
              </Box>
            )}

            <Button
              sx={{
                mt: 3,
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 999,
                px: 3,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
                boxShadow: '0 0 32px rgba(124,58,237,0.45)',
                '&:hover': {
                  background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
                  boxShadow: '0 0 40px rgba(124,58,237,0.55)'
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(230,233,239,0.5)'
                }
              }}
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting || !token}
              onClick={handleSubmit}
            >
              Reset password
            </Button>
          </Box>
        )}

        {resetComplete && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              width: '100%',
              bgcolor: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.25)'
            }}
          >
            <Typography variant="body1" sx={{ color: '#D1FAE5', fontWeight: 600, textAlign: 'center' }}>
              Your password has been updated and you are now signed in.
            </Typography>
          </Box>
        )}

        {resetComplete && (
          <Button
            sx={{
              mt: 2,
              color: '#A78BFA',
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'rgba(167,139,250,0.1)'
              }
            }}
            size="small"
            onClick={continueHandler}
          >
            Continue
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
